import type { CSSProperties } from "react"
import { useEffect, useState } from "react"

import type { EditingVariable } from "@/domains/theme/types"

import { copyToClipboard } from "../copy-to-clipboard"

export const ColorPicker = ({
  variable,
  onUpdate,
  onClose,
}: {
  variable: EditingVariable
  onUpdate: (name: string, value: string) => void
  onClose: () => void
}) => {
  const [hue, setHue] = useState(0)
  const [saturation, setSaturation] = useState(0)
  const [lightness, setLightness] = useState(0)

  // Parse initial HSL values
  useEffect(() => {
    const hslMatch = variable.value.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/)
    if (hslMatch) {
      setHue(parseFloat(hslMatch[1] ?? ""))
      setSaturation(parseFloat(hslMatch[2] ?? ""))
      setLightness(parseFloat(hslMatch[3] ?? ""))
    }
  }, [variable.value])

  const updateColor = (h: number, s: number, l: number) => {
    const newValue = `${h} ${s}% ${l}%`
    onUpdate(variable.name, newValue)
  }

  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHue = parseFloat(e.target.value)
    setHue(newHue)
    updateColor(newHue, saturation, lightness)
  }

  const handleSaturationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSaturation = parseFloat(e.target.value)
    setSaturation(newSaturation)
    updateColor(hue, newSaturation, lightness)
  }

  const handleLightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLightness = parseFloat(e.target.value)
    setLightness(newLightness)
    updateColor(hue, saturation, newLightness)
  }

  const currentColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`

  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
      <div className="pointer-events-auto w-80 rounded-lg bg-secondary-800 p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-secondary-50">Edit {variable.name}</h3>
          <button onClick={onClose} className="text-secondary-400 hover:text-secondary-50">
            ✕
          </button>
        </div>

        {/* Color Preview */}
        <div className="mb-6 flex items-center gap-4">
          <div className="h-16 w-16 rounded border border-secondary-700" style={{ backgroundColor: currentColor }} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-mono text-sm text-secondary-50">
                {hue.toFixed(0)} {saturation.toFixed(1)}% {lightness.toFixed(1)}%
              </div>
              <button
                onClick={() => copyToClipboard(`${hue.toFixed(0)} ${saturation.toFixed(1)}% ${lightness.toFixed(1)}%`)}
                className="text-secondary-400 transition-colors hover:text-secondary-300"
                title="Copy HSL values"
              >
                📋
              </button>
            </div>
            <div className="text-xs text-secondary-400">HSL Values</div>
          </div>
        </div>

        {/* Hue Slider */}
        <div className="mb-4">
          <label className="mb-2 block text-sm text-secondary-300">Hue</label>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="360"
              step="1"
              value={hue}
              onChange={handleHueChange}
              className="via-yellow-500 via-cyan-500 via-purple-500 h-2 w-full cursor-pointer appearance-none rounded-lg bg-gradient-to-r from-red-500 via-blue-500 via-green-500 to-red-500"
            />
            <div className="mt-1 text-xs text-secondary-400">{hue.toFixed(0)}°</div>
          </div>
        </div>

        {/* Saturation Slider */}
        <div className="mb-4">
          <label className="mb-2 block text-sm text-secondary-300">Saturation</label>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={saturation}
              onChange={handleSaturationChange}
              className="to-current h-2 w-full cursor-pointer appearance-none rounded-lg bg-gradient-to-r from-gray-400"
              style={{ "--tw-gradient-to": currentColor } as CSSProperties}
            />
            <div className="mt-1 text-xs text-secondary-400">{saturation.toFixed(1)}%</div>
          </div>
        </div>

        {/* Lightness Slider */}
        <div className="mb-6">
          <label className="mb-2 block text-sm text-secondary-300">Lightness</label>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={lightness}
              onChange={handleLightnessChange}
              className="via-current h-2 w-full cursor-pointer appearance-none rounded-lg bg-gradient-to-r from-black to-white"
              style={{ "--tw-gradient-to": currentColor } as CSSProperties}
            />
            <div className="mt-1 text-xs text-secondary-400">{lightness.toFixed(1)}%</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded bg-secondary-800 px-4 py-2 text-sm text-secondary-50 hover:bg-secondary-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
